import { useState } from "react";

import type {
  PersonListingFilters,
  PersonListingFiltersUnbranded,
  PersonListingSortColumn,
  PersonSearchRequestUnbranded,
} from "@thalamos/haven-common";
import {
  DIRECTORY_PAGE_LIMIT_DEFAULT,
  PersonListingFiltersSchema,
  PersonSearchRequestSchema,
} from "@thalamos/haven-common";

import type { DirectorySort } from "../Admin/DirectoryTable.js";
import { useDirectoryQuery } from "../Admin/useDirectoryQuery.js";

export type PersonListingColumn = PersonListingSortColumn | "upn";

/**
 * What the young people's list is showing: its search, order, filters, toggle
 * and page.
 *
 * Held for the page's lifetime only, so reopening the list starts it again at
 * the first page, alphabetical, with nothing applied. Any change but turning a
 * page goes back to the first, since the page the user was on may not exist in
 * what the listing now holds.
 *
 * Every request it builds parses, because a filter is applied only once it
 * does.
 */
export const usePersonListing = ({
  administering,
}: {
  administering: boolean;
}) => {
  const { query, input, offset, setQuery, setOffset } = useDirectoryQuery();
  const [sort, setSort] = useState<DirectorySort<PersonListingColumn> | null>(
    null,
  );
  const [filters, setFilters] = useState<PersonListingFilters>({});
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  const narrowed =
    query.length > 0 ||
    onlyUnassigned ||
    Object.values(filters).some((filter) => filter !== undefined);

  const request = PersonSearchRequestSchema.parse({
    query,
    offset,
    limit: DIRECTORY_PAGE_LIMIT_DEFAULT,
    ...(sort === null || sort.column === "upn"
      ? {}
      : { sort: { column: sort.column, direction: sort.direction } }),
    ...(narrowed ? { filters } : {}),
    onlyUnassigned,
    // An admin's toggle count is `unassignedCountRequest`, asked for on its own.
    withUnassignedCount: !administering,
  } satisfies PersonSearchRequestUnbranded);

  // Neither count depends on the order or the page, so neither is asked for
  // again when only those change; only the counts are wanted, so each is cut
  // to one row.
  const countsRequest = PersonSearchRequestSchema.parse({
    query,
    offset: 0,
    limit: 1,
    ...(narrowed ? { filters } : {}),
    onlyUnassigned,
    withFacets: true,
  } satisfies PersonSearchRequestUnbranded);

  // An admin's toggle count leaves the toggle itself out, so turning it on
  // does not change the number it is offered with.
  const unassignedCountRequest = PersonSearchRequestSchema.parse({
    query,
    offset: 0,
    limit: 1,
    filters,
    withUnassignedCount: true,
  } satisfies PersonSearchRequestUnbranded);

  return {
    query,
    input,
    offset,
    setQuery,
    setOffset,
    sort,
    setSort: (next: DirectorySort<PersonListingColumn> | null) => {
      setSort(next);
      setOffset(0);
    },
    filters,
    setFilter: <Column extends keyof PersonListingFiltersUnbranded>(
      column: Column,
      value: PersonListingFiltersUnbranded[Column] | undefined,
    ) => {
      setFilters((current) => {
        const next = PersonListingFiltersSchema.safeParse({
          ...current,
          [column]: value,
        });

        return next.success ? next.data : current;
      });
      setOffset(0);
    },
    clearFilters: () => {
      setFilters({});
      setOffset(0);
    },
    onlyUnassigned,
    setOnlyUnassigned: (next: boolean) => {
      setOnlyUnassigned(next);
      setOffset(0);
    },
    /** Whether anything narrows what is listed, as opposed to ordering it. */
    narrowed,
    request,
    countsRequest,
    unassignedCountRequest,
  };
};
